import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { DataTableSkeleton } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const ComparisonTableSchema = z.object({
  metrics: z.array(
    z.object({
      label: z.string(),
      values: z.array(
        z.object({
          keyword: z.string(),
          value: z.string(),
          highlight: z.boolean().optional(),
        }),
      ),
    }),
  ),
});

type ComparisonTableData = z.infer<typeof ComparisonTableSchema>;

/* ────────────────────── Component ────────────────────── */

export function ComparisonTable({
  widget,
  className,
}: AgentWidgetProps<ComparisonTableData>) {
  const { metrics } = widget.data;

  if (metrics.length === 0) return null;

  // 첫 번째 행의 keyword 목록이 헤더가 됨
  const keywords = metrics[0].values.map((v) => v.keyword);

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl border border-stroke-subtle",
        className,
      )}
    >
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-stroke-subtle bg-surface-secondary/60">
            {/* 지표 헤더 (빈 첫 셀) */}
            <th className="py-2.5 pl-4 pr-3 text-left font-medium text-text-tertiary">
              지표
            </th>
            {keywords.map((kw, idx) => (
              <th
                key={idx}
                className="py-2.5 px-3 text-center font-semibold text-text-primary"
              >
                {kw}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric, rowIdx) => (
            <tr
              key={rowIdx}
              className={cn(
                "border-b border-stroke-subtle/50 last:border-0",
                rowIdx % 2 === 1 && "bg-surface-secondary/20",
              )}
            >
              {/* 지표 레이블 */}
              <td className="py-2.5 pl-4 pr-3 font-medium text-text-tertiary">
                {metric.label}
              </td>
              {/* 값 셀 */}
              {metric.values.map((cell, colIdx) => (
                <td
                  key={colIdx}
                  className={cn(
                    "py-2.5 px-3 text-center tabular-nums",
                    cell.highlight
                      ? "bg-accent/10 font-bold text-text-brand"
                      : "text-text-primary",
                  )}
                >
                  {cell.value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function ComparisonTableSkeleton({ className }: { className?: string }) {
  return <DataTableSkeleton columns={4} rows={6} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const comparisonTableWidget = defineWidget<ComparisonTableData>({
  type: WIDGET_TYPES.COMPARISON_TABLE,
  label: "키워드 비교 테이블",
  schema: ComparisonTableSchema,
  component: ComparisonTable,
  skeleton: ComparisonTableSkeleton,
  defaultLayout: "full",
  maxHeight: 360,
  aiHint: "2~4개 키워드를 지표별로 나란히 비교하는 테이블",
});

registerWidget(comparisonTableWidget);
